import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ExternalLink, Users, HardHat, Settings, ChevronRight,
  UserCheck, Briefcase, ClipboardCheck, Camera, Calendar, FileText, Package,
} from "lucide-react";

const PORTALS = [
  {
    id: "customer",
    title: "Customer Portal",
    description: "Homeowner-facing project tracker with quote approval, contract signing, job status, and document access.",
    icon: UserCheck,
    path: "/portal",
    status: "Live" as const,
    features: ["Quote Summary", "Contract Signing", "Job Status", "Documents (Read-Only)", "Support Requests"],
  },
  {
    id: "contractor",
    title: "Contractor Portal",
    description: "Subcontractor-facing portal for assigned jobs, schedule visibility, QA checklists, and photo uploads.",
    icon: HardHat,
    path: "/contractor-portal",
    status: "Live" as const,
    features: ["Assigned Jobs", "Schedule View", "QA Checklists", "Photo Upload"],
  },
  {
    id: "vendor",
    title: "Vendor Portal",
    description: "Supplier-facing portal for purchase orders, delivery tracking, and invoicing.",
    icon: Package,
    path: "#",
    status: "Planned" as const,
    features: ["Purchase Orders", "Delivery Tracking", "Invoicing"],
  },
  {
    id: "partner",
    title: "Partner Portal",
    description: "Strategic partner access for referral tracking, co-branded assets, and shared reporting.",
    icon: Users,
    path: "#",
    status: "Planned" as const,
    features: ["Referral Tracking", "Shared Reports", "Co-Branded Assets"],
  },
];

const STATUS_STYLE: Record<string, string> = {
  Live: "bg-success/10 text-success border-success/20",
  Planned: "bg-muted text-muted-foreground border-border",
};

export default function PortalsHub() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <ExternalLink className="h-5 w-5 text-primary" /> Portals
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            External-facing experiences for customers, contractors, and partners.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/settings"><Settings className="h-3.5 w-3.5 mr-1.5" /> Portal Settings</Link>
        </Button>
      </div>

      {/* Portal Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PORTALS.map((portal) => {
          const Icon = portal.icon;
          const isLive = portal.status === "Live";
          return (
            <Card key={portal.id} className={`transition-all ${isLive ? "hover:shadow-md hover:border-primary/30" : "opacity-70"}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-semibold">{portal.title}</CardTitle>
                      <Badge variant="outline" className={`text-[9px] mt-1 ${STATUS_STYLE[portal.status]}`}>
                        {portal.status}
                      </Badge>
                    </div>
                  </div>
                  {isLive && (
                    <Button variant="ghost" size="sm" asChild className="text-xs">
                      <Link to={portal.path}>
                        Preview <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                      </Link>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground leading-relaxed">{portal.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {portal.features.map((f) => (
                    <Badge key={f} variant="secondary" className="text-[10px] font-normal">
                      {f}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info */}
      <Card className="border-dashed">
        <CardContent className="py-4">
          <p className="text-xs text-muted-foreground text-center">
            Portals provide controlled, external-facing experiences that are separate from internal operations.
            Configure branding, access, and content in <Link to="/settings" className="text-primary hover:underline font-medium">Settings</Link>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
